import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatINR, todayISO, formatDate } from "../lib/api";
import { Plus, Printer, Trash2, X, Search, Pencil, ArrowRightCircle } from "lucide-react";
import { generateQuotePdf } from "../lib/pdf";
import { COMPANY_BRANDS } from "../lib/brands";
import { DirectoryPicker } from "../components/DirectoryPicker";
import { toast } from "sonner";
import { usePagination } from "../hooks/usePagination";
import { Pagination } from "../components/Pagination";

const blankItem = { description: "", quantity: 1, rate: 0, amount: 0 };
const blank = {
  quote_date: todayISO(),
  valid_until: "",
  quote_number: "",
  customer_name: "", customer_address: "", customer_gst: "", customer_attn: "",
  from_location: "", to_location: "",
  items: [{ ...blankItem }],
  freight_estimate: 0,
  subtotal: 0, total: 0,
  status: "draft", notes: "",
  company_brand: "ito_engineering",
};

function quoteBadge(status) {
  if (status === "converted") return "badge-success";
  if (status === "accepted") return "badge-neutral";
  if (status === "sent") return "badge-pending";
  return "badge-pending";
}

export default function Quotations() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const paged = usePagination(items, 25);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [company, setCompany] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [customers, setCustomers] = useState([]);

  const load = useCallback(async () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (q) params.q = q;
    const { data } = await api.get("/quotations", { params });
    setItems(data);
  }, [statusFilter, q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/company").then((r) => setCompany(r.data)).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  function pickCustomer(name) {
    const c = customers.find((x) => x.name === name);
    setForm((f) => ({ ...f, customer_name: name, customer_gst: c?.gst_number ?? f.customer_gst, customer_address: c?.address ?? f.customer_address }));
  }

  function recalc(updated) {
    const sub = (updated.items || []).reduce((s, it) => s + Number(it.amount || 0), 0);
    const total = sub + Number(updated.freight_estimate || 0);
    return { ...updated, subtotal: +sub.toFixed(2), total: +total.toFixed(2) };
  }

  function updateItem(i, k, v) {
    const arr = form.items.slice();
    arr[i] = { ...arr[i], [k]: v };
    if (k === "quantity" || k === "rate") arr[i].amount = +(Number(arr[i].quantity || 0) * Number(arr[i].rate || 0)).toFixed(2);
    setForm(recalc({ ...form, items: arr }));
  }
  function addItem() { setForm(recalc({ ...form, items: [...form.items, { ...blankItem }] })); }
  function removeItem(i) { setForm(recalc({ ...form, items: form.items.filter((_, idx) => idx !== i) })); }

  function openNew() {
    setForm(blank); setEditingId(null); setShowForm(true);
    api.get("/quotations/next-number").then((r) => { if (r.data?.next) setForm((f) => (f.quote_number ? f : { ...f, quote_number: r.data.next })); }).catch(() => {});
  }
  function openEdit(qt) { setForm({ ...blank, ...qt }); setEditingId(qt.id); setShowForm(true); }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) await api.put(`/quotations/${editingId}`, form);
      else await api.post("/quotations", form);
      setShowForm(false); setForm(blank); setEditingId(null); load();
      toast.success(editingId ? "Quotation updated" : "Quotation created");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save quotation");
    } finally { setSaving(false); }
  }

  async function remove(id) {
    if (!confirm("Delete quotation?")) return;
    await api.delete(`/quotations/${id}`); load();
  }

  async function convert(qt) {
    if (!confirm(`Convert ${qt.quote_number} into a new Lorry Receipt?`)) return;
    try {
      const { data } = await api.post(`/quotations/${qt.id}/convert`);
      toast.success(`Created ${data.lr_number}`);
      navigate(`/lr/${data.lr_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Conversion failed");
    }
  }

  return (
    <div data-testid="quotations-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Sales</div>
          <h1 className="font-headings text-4xl font-bold text-[#1A1A18] mt-1">Quotations</h1>
        </div>
        <button className="btn-primary" onClick={openNew} data-testid="create-quotation-button"><Plus className="w-4 h-4" /> New Quotation</button>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#68665E]" />
          <input className="input-field pl-9 w-72" placeholder="Search quote or customer..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="quotation-search" />
        </div>
        <select className="input-field w-40" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="quotation-status-filter">
          <option value="">All status</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="accepted">Accepted</option>
          <option value="converted">Converted</option>
        </select>
      </div>

      <Pagination {...paged} testId="quotations-pagination" />
      <div className="table-wrap">
        <table className="w-full">
          <thead>
            <tr>
              <th className="table-th">Quote #</th>
              <th className="table-th">Date</th>
              <th className="table-th">Valid Until</th>
              <th className="table-th">Customer</th>
              <th className="table-th">Route</th>
              <th className="table-th">Total</th>
              <th className="table-th">Status</th>
              <th className="table-th text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td className="table-td text-[#68665E] text-center" colSpan={8}>No quotations yet.</td></tr>
            ) : paged.pageItems.map((qt) => (
              <tr key={qt.id} className="table-row" data-testid={`quotation-row-${qt.id}`}>
                <td className="table-td font-mono text-xs">{qt.quote_number}</td>
                <td className="table-td">{formatDate(qt.quote_date)}</td>
                <td className="table-td">{formatDate(qt.valid_until) || "—"}</td>
                <td className="table-td">{qt.customer_name || "—"}</td>
                <td className="table-td text-xs">{qt.from_location || "—"} → {qt.to_location || "—"}</td>
                <td className="table-td font-medium">{formatINR(qt.total)}</td>
                <td className="table-td">
                  <span className={quoteBadge(qt.status)} data-testid={`quotation-status-${qt.id}`}>{qt.status}</span>
                  {qt.converted_lr_number && (
                    <button className="block text-[10px] text-[#C84B31] hover:underline mt-1" onClick={() => navigate(`/lr/${qt.converted_lr_id}`)}>{qt.converted_lr_number}</button>
                  )}
                </td>
                <td className="table-td text-right whitespace-nowrap">
                  {qt.status !== "converted" && (
                    <button className="btn-ghost text-[#2F6F4E]" title="Convert to LR" onClick={() => convert(qt)} data-testid={`quotation-convert-${qt.id}`}><ArrowRightCircle className="w-4 h-4" /></button>
                  )}
                  <button className="btn-ghost" onClick={() => openEdit(qt)} data-testid={`quotation-edit-${qt.id}`}><Pencil className="w-4 h-4" /></button>
                  <button className="btn-ghost" onClick={() => generateQuotePdf(qt, company)} data-testid={`quotation-print-${qt.id}`}><Printer className="w-4 h-4" /></button>
                  <button className="btn-ghost text-[#8A2526]" onClick={() => remove(qt.id)} data-testid={`quotation-delete-${qt.id}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-start justify-center overflow-y-auto py-8" data-testid="quotation-form-modal">
          <div className="bg-white w-full max-w-5xl mx-4">
            <div className="px-6 py-4 border-b border-[#E5E3DB] flex items-center justify-between">
              <h2 className="font-headings text-xl font-bold">{editingId ? "Edit Quotation" : "Create Quotation"}</h2>
              <button className="btn-ghost" onClick={() => { setShowForm(false); setEditingId(null); }} data-testid="quotation-form-close"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-6">
              <div>
                <div className="section-title">Quotation</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div><label className="input-label">Quote Number {editingId ? "" : "(blank = auto)"}</label><input className="input-field font-mono" value={form.quote_number || ""} onChange={(e) => setForm({ ...form, quote_number: e.target.value })} placeholder={editingId ? "" : "Auto-generate"} data-testid="quotation-number-input" /></div>
                  <div><label className="input-label">Date</label><input type="date" className="input-field" value={form.quote_date} onChange={(e) => setForm({ ...form, quote_date: e.target.value })} data-testid="quotation-date" /></div>
                  <div><label className="input-label">Valid Until</label><input type="date" className="input-field" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} data-testid="quotation-valid-until" /></div>
                  <div><label className="input-label">Status</label>
                    <select className="input-field" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="quotation-status-select">
                      <option value="draft">Draft</option>
                      <option value="sent">Sent</option>
                      <option value="accepted">Accepted</option>
                      <option value="converted">Converted</option>
                    </select>
                  </div>
                  <div><label className="input-label">Issue Under (Company on PDF)</label>
                    <select className="input-field" value={form.company_brand || "ito_engineering"} onChange={(e) => setForm({ ...form, company_brand: e.target.value })} data-testid="quotation-company-brand">
                      {COMPANY_BRANDS.map((b)=><option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <div className="section-title">Customer <span className="text-[11px] font-normal text-[#68665E]">(pick from directory — GST &amp; address auto-fill)</span></div>
                <DirectoryPicker
                  testPrefix="quotation-customer"
                  kind="all"
                  value={{ company: form.customer_name, gst: form.customer_gst, address: form.customer_address, poc_name: form.customer_attn }}
                  onSelect={(p) => setForm((f) => ({ ...f,
                    ...(p.company !== undefined ? { customer_name: p.company } : {}),
                    ...(p.gst !== undefined ? { customer_gst: p.gst } : {}),
                    ...(p.address !== undefined ? { customer_address: p.address } : {}),
                    ...(p.poc_name !== undefined ? { customer_attn: p.poc_name } : {}),
                  }))}
                />
              </div>

              <div>
                <div className="section-title">Route</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="input-label">From</label><input className="input-field" value={form.from_location} onChange={(e) => setForm({ ...form, from_location: e.target.value })} data-testid="quotation-from" /></div>
                  <div><label className="input-label">To</label><input className="input-field" value={form.to_location} onChange={(e) => setForm({ ...form, to_location: e.target.value })} data-testid="quotation-to" /></div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="section-title mb-0">Line Items</div>
                  <button type="button" className="btn-ghost text-xs" onClick={addItem} data-testid="quotation-add-item"><Plus className="w-3 h-3" /> Add Item</button>
                </div>
                <div className="border border-[#E5E3DB]">
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        <th className="table-th">Description</th>
                        <th className="table-th w-20">Qty</th>
                        <th className="table-th w-32">Rate</th>
                        <th className="table-th w-32">Amount</th>
                        <th className="table-th w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.items.map((it, i) => (
                        <tr key={i} className="border-b border-[#E5E3DB] last:border-0">
                          <td className="p-1"><input className="input-field border-0 focus:ring-0" value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} data-testid={`quotation-item-desc-${i}`} /></td>
                          <td className="p-1"><input type="number" step="0.01" className="input-field border-0 focus:ring-0" value={it.quantity} onChange={(e) => updateItem(i, "quantity", Number(e.target.value))} data-testid={`quotation-item-qty-${i}`} /></td>
                          <td className="p-1"><input type="number" step="0.01" className="input-field border-0 focus:ring-0" value={it.rate} onChange={(e) => updateItem(i, "rate", Number(e.target.value))} data-testid={`quotation-item-rate-${i}`} /></td>
                          <td className="p-1 px-3 font-medium">{formatINR(it.amount)}</td>
                          <td className="p-1 text-center"><button type="button" onClick={() => removeItem(i)} className="text-[#8A2526]"><Trash2 className="w-4 h-4" /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end">
                <div className="w-72 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-[#68665E]">Subtotal</span><span className="font-medium" data-testid="quotation-subtotal">{formatINR(form.subtotal)}</span></div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[#68665E]">Freight Estimate</span>
                    <input type="number" step="0.01" className="input-field w-32 text-right" value={form.freight_estimate} onChange={(e) => setForm(recalc({ ...form, freight_estimate: Number(e.target.value) }))} data-testid="quotation-freight-estimate" />
                  </div>
                  <div className="flex justify-between border-t border-[#E5E3DB] pt-2 text-base"><span className="font-bold">Estimated Total</span><span className="font-bold" data-testid="quotation-total">{formatINR(form.total)}</span></div>
                </div>
              </div>

              <div>
                <label className="input-label">Notes</label>
                <textarea rows={2} className="input-field" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="quotation-notes" />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E3DB]">
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" disabled={saving} className="btn-primary" data-testid="quotation-save-button">{saving ? "Saving..." : (editingId ? "Update Quotation" : "Create Quotation")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
